/**
 * Dedicated slice for api/get-onedrive-docs used by Check-in New list.
 * State: loading, isError, message, docs. No legacy action dispatches.
 */
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getOneDriveDocs } from "../../api/documentsAPI";
import UserAuth from "../../../auth/userAuth";
import { logger } from "../../../utils/logger";
import { deleteOneDriveDocument } from "./oneDriveDeleteSlice";

const userAuth = new UserAuth();
const CHANNEL_DOC_ROOT = "ChannelDoc";
const HIDDEN_CHANNEL_FOLDER_NAME_REGEX = /^channel-config\s*\(do not delete\)$/i;

function shouldHideChannelConfigFolder(item, fileRoot) {
  if ((fileRoot ?? "").toLowerCase() !== CHANNEL_DOC_ROOT.toLowerCase()) {
    return false;
  }
  if (item?.folder == null) {
    return false;
  }
  const itemName = typeof item?.name === "string" ? item.name.trim() : "";
  return HIDDEN_CHANNEL_FOLDER_NAME_REGEX.test(itemName);
}

function filterHiddenChannelFolders(list, fileRoot) {
  const docs = Array.isArray(list) ? list : [];
  return docs.filter((item) => !shouldHideChannelConfigFolder(item, fileRoot));
}

export const fetchOneDriveDocsList = createAsyncThunk(
  "oneDriveDocsList/fetch",
  async (
    {
      teamId,
      channelId,
      tenantId,
      pageSize,
      recFrom,
      searchText,
      documentType,
      fileRoot,
      isOnedrive,
      folderId,
    },
    { rejectWithValue }
  ) => {
    logger.info("fetchOneDriveDocsList start", { teamId, channelId, recFrom, folderId }, "REDUX");
    const tokenResponse = await userAuth.getUserToken(true);
    const accessToken = tokenResponse?.accessToken ?? null;
    if (!accessToken) {
      logger.error("fetchOneDriveDocsList no access token", tokenResponse, "REDUX");
      return rejectWithValue({ message: "No access token" });
    }

    const recSize = pageSize ?? 20;

    try {
      const { data, status } = await getOneDriveDocs(accessToken, {
        teamId: teamId ?? "",
        channelId: channelId ?? "",
        tenantId: tenantId ?? "",
        fileType: documentType ?? "",
        searchText: searchText ?? "",
        recFrom: recFrom ?? 0,
        recSize,
        oneDriveRoot: fileRoot ?? "",
        isOnedrive: isOnedrive ?? false,
        folderId: folderId ?? "",
      });

      if (status === 200) {
        const value = data?.value != null ? data.value : [];
        const unfilteredList = Array.isArray(value) ? value : [];
        const list = filterHiddenChannelFolders(unfilteredList, fileRoot ?? "");
        const hiddenCount = Math.max(0, unfilteredList.length - list.length);
        const rawTotalCount =
          typeof data?.totalCount === "number"
            ? data.totalCount
            : unfilteredList.length;
        const totalCount = Math.max(0, rawTotalCount - hiddenCount);
        return { docs: list, totalCount, raw: data };
      }

      if (status === 204) {
        return { docs: [], totalCount: 0, raw: null };
      }

      logger.error("fetchOneDriveDocsList non-200/204", { status }, "REDUX");
      return rejectWithValue({
        message: data?.errorMessage ?? `Request failed (${status})`,
      });
    } catch (err) {
      logger.error("fetchOneDriveDocsList failed", err, "REDUX");
      const message =
        err?.response?.data?.message ??
        err?.message ??
        "Failed to load documents";
      return rejectWithValue({ message });
    }
  }
);

const initialState = {
  docs: [],
  totalCount: 0,
  loading: false,
  isError: false,
  message: "",
  /** True after at least one list fetch completed (used to avoid refetch on check-in cancel). */
  initialized: false,
  /** Incremented so Check-in New list can refetch from accordion (same pattern as All Documents search). */
  refreshTrigger: 0,
  /** Latest in-flight client request id; older responses are ignored. */
  activeClientRequestId: 0,
};

const oneDriveDocsListSlice = createSlice({
  name: "oneDriveDocsList",
  initialState,
  reducers: {
    clearOneDriveDocsListError: (state) => {
      state.isError = false;
      state.message = "";
    },
    resetOneDriveDocsList: () => initialState,
    requestRefreshCheckInNewList: (state) => {
      state.refreshTrigger = (state.refreshTrigger || 0) + 1;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOneDriveDocsList.pending, (state, action) => {
        state.loading = true;
        state.isError = false;
        state.message = "";
        if (action.meta.arg?.clientRequestId != null) {
          state.activeClientRequestId = action.meta.arg.clientRequestId;
        }
      })
      .addCase(fetchOneDriveDocsList.fulfilled, (state, action) => {
        const reqId = action.meta.arg?.clientRequestId;
        if (
          reqId != null &&
          reqId !== state.activeClientRequestId
        ) {
          return;
        }
        state.loading = false;
        state.isError = false;
        state.message = "";
        state.docs = action.payload?.docs ?? [];
        state.totalCount = action.payload?.totalCount ?? 0;
        state.initialized = true;
      })
      .addCase(fetchOneDriveDocsList.rejected, (state, action) => {
        const reqId = action.meta.arg?.clientRequestId;
        if (
          reqId != null &&
          reqId !== state.activeClientRequestId
        ) {
          return;
        }
        state.loading = false;
        state.isError = true;
        state.message = action.payload?.message ?? "Unknown error";
        state.docs = [];
        state.totalCount = 0;
        state.initialized = true;
      })
      .addCase(deleteOneDriveDocument.fulfilled, (state, action) => {
        const fileId = action.payload?.fileId;
        if (!fileId) return;
        state.docs = (state.docs ?? []).filter((d) => d?.id !== fileId);
        state.totalCount = Math.max(0, state.totalCount - 1);
      });
  },
});

export const {
  clearOneDriveDocsListError,
  resetOneDriveDocsList,
  requestRefreshCheckInNewList,
} = oneDriveDocsListSlice.actions;
export default oneDriveDocsListSlice.reducer;
